import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, map, Observable, ReplaySubject } from 'rxjs';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { AcceptanceCriteriaStatus } from 'src/app/shared/models/enums/acceptance-criteria-status.enum';
import { AcceptanceCriteriaType } from 'src/app/shared/models/enums/acceptance-criteria-type.enum';
import { KeyValue } from 'src/app/shared/models/key-value.model';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { combine } from 'src/app/shared/utilities/route';
import { EsignTemplate } from '../models/doc-admin-esign-template.model';
import { DocAdminPermissions } from '../models/doc-admin-permissions.model';
import { selectProfessionTree } from '../store/selectors/profession-specialty-configuration.selector';
import { interpolateParams } from 'src/app/shared/utilities/interpolate-params';
import { LineOfBusiness } from 'src/app/shared/models/enums/line-of-business';

@Injectable({ providedIn: 'root' })
export class DocAdminHelperService {
    private readonly MAX_FILENAME_LENGTH = 32;
    private readonly coreApiAdminPath: string;
    private readonly coreApiDocTypesPath: string;

    private readonly currentStates$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    private readonly currentSnapshotStates$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

    private readonly currentFacilityTypes$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    private readonly currentSnapshotFacilityTypes$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);

    private readonly currentProfessionAndSpecialtySettings$: ReplaySubject<string[]> = new ReplaySubject<string[]>(1);
    private readonly currentSnapShotProfessionAndSpecialtySettings$: ReplaySubject<string[]> = new ReplaySubject<
        string[]
    >(1);

    private readonly currentRelatedSpecialtiesSelectAllLog$: ReplaySubject<number[]> = new ReplaySubject<number[]>(1);
    private readonly snapshotRelatedSpecialtiesSelectAllLog$: ReplaySubject<number[]> = new ReplaySubject<number[]>(1);

    private readonly currentFacilityLinesOfBusiness$: ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> =
        new ReplaySubject<KeyValue<number, KeyValue<string, boolean>>>(1);
    private readonly currentSnapshotFacilityLinesOfBusiness$: ReplaySubject<
        KeyValue<number, KeyValue<string, boolean>>
    > = new ReplaySubject<KeyValue<number, KeyValue<string, boolean>>>(1);

    private readonly currentSampleFile$: ReplaySubject<File> = new ReplaySubject<File>(1);
    private readonly currentSampleBlob$: ReplaySubject<Blob> = new ReplaySubject<Blob>(1);
    private readonly currentCandidateReviewFile$: ReplaySubject<File> = new ReplaySubject<File>(1);
    private readonly currentCandidateReviewBlob$: ReplaySubject<Blob> = new ReplaySubject<Blob>(1);

    private readonly existingDocuSignTemplates$: BehaviorSubject<EsignTemplate[]> = new BehaviorSubject<
        EsignTemplate[]
    >(undefined);
    private readonly selectedDocuSignTemplates$: BehaviorSubject<EsignTemplate[]> = new BehaviorSubject<
        EsignTemplate[]
    >(undefined);

    private readonly snapshotUpdated$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

    private readonly currentExternalInstructions$: BehaviorSubject<string> = new BehaviorSubject<string>(undefined);

    isNoStatesOrSpecialties$: Observable<boolean> = combineLatest({
        availableProfessions: this.store.select(selectProfessionTree).pipe(map((tree) => tree?.professionTypes || [])),
        currentProfessionsAndSpecialties: this.currentProfessionAndSpecialtySettings$,
        currentLinesOfBusiness: this.currentFacilityLinesOfBusiness$,
        currentStates: this.currentStates$
    }).pipe(
        map(({ availableProfessions, currentProfessionsAndSpecialties, currentLinesOfBusiness, currentStates }) => {
            if (
                !currentProfessionsAndSpecialties ||
                !currentLinesOfBusiness ||
                !currentStates ||
                !availableProfessions
            ) {
                return false;
            }
            const hasNoSpecialties =
                (currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length ===
                    availableProfessions.length ||
                    currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length === 0) &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('P')).length === 0 &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('S')).length === 0;
            const hasNoLinesOfBusiness =
                Object.entries(currentLinesOfBusiness).some(
                    (lob) => lob[1].direct === true || lob[1].subcontractor === true
                ) === false;
            const hasNoStates = currentStates.length === 0;
            return hasNoSpecialties && hasNoLinesOfBusiness && hasNoStates;
        })
    );

    isNoStatesOrSpecialtiesSnapshot$: Observable<boolean> = combineLatest({
        availableProfessions: this.store.select(selectProfessionTree).pipe(map((tree) => tree?.professionTypes || [])),
        currentProfessionsAndSpecialties: this.currentSnapShotProfessionAndSpecialtySettings$,
        currentLinesOfBusiness: this.currentSnapshotFacilityLinesOfBusiness$,
        currentStates: this.currentSnapshotStates$
    }).pipe(
        map(({ availableProfessions, currentProfessionsAndSpecialties, currentLinesOfBusiness, currentStates }) => {
            if (
                !currentProfessionsAndSpecialties ||
                !currentLinesOfBusiness ||
                !currentStates ||
                !availableProfessions
            ) {
                return false;
            }

            const hasNoLinesOfBusiness =
                Object.entries(currentLinesOfBusiness).some(
                    (lob) => lob[1].direct === true || lob[1].subcontractor === true
                ) === false;
            const hasNoStates = currentStates.length === 0;
            const hasNoSpecialties =
                (currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length ===
                    availableProfessions.length ||
                    currentProfessionsAndSpecialties.filter((s) => s.startsWith('T')).length === 0) &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('P')).length === 0 &&
                currentProfessionsAndSpecialties.filter((s) => s.startsWith('S')).length === 0;

            return hasNoSpecialties && hasNoStates && hasNoLinesOfBusiness;
        })
    );

    constructor(
        private readonly http: HttpClient,
        @Inject(APP_CONFIG) readonly settings: Settings,
        private readonly store: Store<{}>,
        private readonly _ldManager: LDFeatureManager
    ) {
        this.coreApiAdminPath = combine(settings.coreAPIUrl, 'ayanova', 'compliance', 'document-admin');
        this.coreApiDocTypesPath = combine(settings.coreAPIUrl, 'ayanova', 'compliance', 'document-types');
    }

    get CurrentStates$(): ReplaySubject<string[]> {
        return this.currentStates$;
    }

    get CurrentSnapshotStates$(): ReplaySubject<string[]> {
        return this.currentSnapshotStates$;
    }

    get CurrentFacilityTypes$(): ReplaySubject<string[]> {
        return this.currentFacilityTypes$;
    }

    get CurrentSnapshotFacilityTypes$(): ReplaySubject<string[]> {
        return this.currentSnapshotFacilityTypes$;
    }

    get CurrentProfessionAndSpecialtySettings$(): ReplaySubject<string[]> {
        return this.currentProfessionAndSpecialtySettings$;
    }

    get SnapshotProfessionAndSpecialtySettings$(): ReplaySubject<string[]> {
        return this.currentSnapShotProfessionAndSpecialtySettings$;
    }

    get CurrentRelatedSpecialtiesSelectAllLog$(): ReplaySubject<number[]> {
        return this.currentRelatedSpecialtiesSelectAllLog$;
    }
    get SnapshotRelatedSpecialtiesSelectAllLog$(): ReplaySubject<number[]> {
        return this.snapshotRelatedSpecialtiesSelectAllLog$;
    }

    get CurrentFacilityLinesOfBusiness$(): ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> {
        return this.currentFacilityLinesOfBusiness$;
    }

    get CurrentSnapshotFacilityLinesOfBusiness$(): ReplaySubject<KeyValue<number, KeyValue<string, boolean>>> {
        return this.currentSnapshotFacilityLinesOfBusiness$;
    }

    get CurrentSampleFile$(): ReplaySubject<File> {
        return this.currentSampleFile$;
    }

    get CurrentSampleBlob$(): ReplaySubject<Blob> {
        return this.currentSampleBlob$;
    }

    get CurrentCandidateReviewFile$(): ReplaySubject<File> {
        return this.currentCandidateReviewFile$;
    }

    get CurrentCandidateReviewBlob$(): ReplaySubject<Blob> {
        return this.currentCandidateReviewBlob$;
    }

    get ExistingDocuSignTemplates$(): BehaviorSubject<EsignTemplate[]> {
        return this.existingDocuSignTemplates$;
    }

    get SelectedDocuSignTemplates$(): BehaviorSubject<EsignTemplate[]> {
        return this.selectedDocuSignTemplates$;
    }

    get SnapshotUpdated$(): BehaviorSubject<boolean> {
        return this.snapshotUpdated$;
    }

    get CurrentExternalInstructions$(): BehaviorSubject<string> {
        return this.currentExternalInstructions$;
    }

    get AcceptanceCriteriaTypeOptions(): KeyValue<number, string>[] {
        return [
            {
                id: AcceptanceCriteriaType.None,
                key: AcceptanceCriteriaType.None,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.None)
            },
            {
                id: AcceptanceCriteriaType.AcceptanceCriteriaNotAvailable,
                key: AcceptanceCriteriaType.AcceptanceCriteriaNotAvailable,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.AcceptanceCriteriaNotAvailable)
            },
            {
                id: AcceptanceCriteriaType.LowestLevel,
                key: AcceptanceCriteriaType.LowestLevel,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.LowestLevel)
            },
            {
                id: AcceptanceCriteriaType.CommonAcceptanceCriteria,
                key: AcceptanceCriteriaType.CommonAcceptanceCriteria,
                name: AcceptanceCriteriaType.toName(AcceptanceCriteriaType.CommonAcceptanceCriteria)
            }
        ];
    }

    get AcceptanceCriteriaStatusOptions(): KeyValue<number, string>[] {
        return [
            {
                id: AcceptanceCriteriaStatus.NotReviewed,
                key: AcceptanceCriteriaStatus.NotReviewed,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.NotReviewed)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedNotNeeded,
                key: AcceptanceCriteriaStatus.ReviewedNotNeeded,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedNotNeeded)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedEnabled,
                key: AcceptanceCriteriaStatus.ReviewedEnabled,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedEnabled)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedManualNeeded,
                key: AcceptanceCriteriaStatus.ReviewedManualNeeded,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedManualNeeded)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedDisableAutomatch,
                key: AcceptanceCriteriaStatus.ReviewedDisableAutomatch,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedDisableAutomatch)
            },
            {
                id: AcceptanceCriteriaStatus.ReviewedAlwaysRequireNew,
                key: AcceptanceCriteriaStatus.ReviewedAlwaysRequireNew,
                name: AcceptanceCriteriaStatus.toName(AcceptanceCriteriaStatus.ReviewedAlwaysRequireNew)
            }
        ];
    }

    setCurrentStates(value: string[]): void {
        this.currentStates$.next(value);
    }

    setCurrentSnapshotStates(value: string[]): void {
        this.currentSnapshotStates$.next(value);
    }

    setCurrentFacilityTypes(value: string[]): void {
        this.currentFacilityTypes$.next(value);
    }

    setCurrentSnapshotFacilityTypes(value: string[]): void {
        this.currentSnapshotFacilityTypes$.next(value);
    }

    setCurrentProfessionAndSpecialtySettings(value: string[]): void {
        this.currentProfessionAndSpecialtySettings$.next(value);
    }

    setCurrentSnapShotProfessionAndSpecialtySettings(value: string[]): void {
        this.currentSnapShotProfessionAndSpecialtySettings$.next(value);
    }

    setCurrentRelatedSpecialtiesSelectAllLog(value: number[]): void {
        this.currentRelatedSpecialtiesSelectAllLog$.next(value);
    }

    setSnapshotRelatedSpecialtiesSelectAllLog(value: number[]): void {
        this.snapshotRelatedSpecialtiesSelectAllLog$.next(value);
    }

    setCurrentFacilityLinesOfBusiness(value: KeyValue<number, KeyValue<string, boolean>>): void {
        this.currentFacilityLinesOfBusiness$.next(value);
    }

    setCurrentSnapshotFacilityLinesOfBusiness(value: KeyValue<number, KeyValue<string, boolean>>): void {
        this.currentSnapshotFacilityLinesOfBusiness$.next(value);
    }

    setCurrentSampleFile(file: File): void {
        this.currentSampleFile$.next(file);
    }

    setCurrentSampleBlob(blob: Blob): void {
        this.currentSampleBlob$.next(blob);
    }

    setCurrentCandidateReviewFile(file: File): void {
        this.currentCandidateReviewFile$.next(file);
    }

    setCurrentCandidateReviewBlob(blob: Blob): void {
        this.currentCandidateReviewBlob$.next(blob);
    }

    setExistingDocuSignTemplates(templates: EsignTemplate[]): void {
        this.existingDocuSignTemplates$.next(templates);
    }

    setSelectedDocuSignTemplates(templates: EsignTemplate[]): void {
        this.selectedDocuSignTemplates$.next(templates);
    }

    setSnapshotUpdated(isUpdated: boolean): void {
        this.snapshotUpdated$.next(isUpdated);
    }

    setCurrentExternalInstructions(externalInstructions: string): void {
        this.currentExternalInstructions$.next(externalInstructions);
    }

    // ** Does not exist.
    getPermissions(): Observable<DocAdminPermissions> {
        const url = `${this.coreApiAdminPath}/permissions`;
        return this.http.get<DocAdminPermissions>(url);
    }

    getDocumentTypesList(name: string = null): Observable<unknown[]> {
        const params = interpolateParams({
            lineOfBusiness: LineOfBusiness.PerDiem,
            name: name
        });
        return this.http.get<unknown[]>(this.coreApiDocTypesPath, { params });
    }

    uploadSupplementalFile(file: File): Observable<unknown> {
        const url = `${this.coreApiDocTypesPath}/UploadFile`;
        const data = new FormData();
        data.append('file', file);
        return this.http.post<unknown>(url, data);
    }

    formatFileName(fileName: string): string {
        if (fileName.length <= this.MAX_FILENAME_LENGTH) {
            return fileName;
        }

        const lastDotIndex = fileName.lastIndexOf('.');
        const extension = lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : '';
        const nameWithoutExt = lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;

        const availableLength = this.MAX_FILENAME_LENGTH - 3 - extension.length;

        if (availableLength <= 0) {
            return `${fileName.slice(0, this.MAX_FILENAME_LENGTH - 3)}...`;
        }

        return `${nameWithoutExt.slice(0, availableLength)}... ${extension}`;
    }

    formatFileSize(bytes: number): string {
        if (bytes < 1024) {
            return `${bytes} B`;
        }
        if (bytes < 1024 * 1024) {
            return `${Math.round(bytes / 1024)} KB`;
        }
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
}
